// src/pages/purchase/ViewPurchase.jsx
import * as React from "react";
import axios from "axios";
import Header from "../../components/Header";
import SimpleTable from "../../components/SimpleTable";
import {
  Box,
  Button,
  Snackbar,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Typography,
  Divider,
  useTheme,
  MenuItem,
  Menu,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PrintIcon from "@mui/icons-material/Print";
import { tokens } from "../../theme";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

/* ---------- API ---------- */

const token = localStorage.getItem("token");
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  timeout: 20000,
  headers: {
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`,
  },
});

async function getJSON(path, params) {
  try {
    const res = await api.get(path, { params });
    return { data: res.data?.data || [], error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

async function deleteJSON(path) {
  try {
    const res = await api.delete(path);
    return { data: res.data, error: null };
  } catch (err) {
    const msg =
      err?.response?.data?.message ||
      err?.response?.data?.errors ||
      err.message ||
      "Request failed";
    return { data: null, error: msg };
  }
}

/* ---------- Helper ---------- */
const fmt = (v) => (v == null ? "0.00" : Number(v).toFixed(2));

/* ---------- Page ---------- */
export default function ViewPurchase() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();

  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [detailRow, setDetailRow] = React.useState(null);
  const [deleteRow, setDeleteRow] = React.useState(null);
  const [deleting, setDeleting] = React.useState(false);
  const [toast, setToast] = React.useState({
    open: false,
    msg: "",
    sev: "success",
  });

  const [statusMenu, setStatusMenu] = React.useState({
    anchorEl: null,
    row: null,
  });

  /* ---------- Step 1: Load Purchases ---------- */
  const loadPurchases = React.useCallback(async () => {
    setLoading(true);
    const { data, error } = await getJSON("/api/purchase", { _: Date.now() });
    setLoading(false);

    if (error) {
      setToast({ open: true, msg: "Failed to load purchases", sev: "error" });
      setRows([]);
      return;
    }

    const mapped = data.map((p) => ({
      ...p,
      supplierName: p.supplier?.name || "",
      warehouseName: p.warehouse?.name || "",
    }));

    setRows(mapped);
  }, []);

  /* ---------- Step 2: Load on mount ---------- */
  React.useEffect(() => {
    loadPurchases();
  }, [loadPurchases]);

  const handleStatusChange = async (row, newStatus) => {
    if (!row?.id) return;

    // Only pending can be updated
    if (row.status !== "pending") {
      setToast({ open: true, msg: "Status already changed.", sev: "error" });
      return;
    }

    const prevRows = [...rows];

    // Optimistic update
    setRows((prev) =>
      prev.map((r) => (r.id === row.id ? { ...r, status: newStatus } : r)),
    );

    try {
      const res = await api.patch(`/api/purchase/status/${row.id}`, {
        status: newStatus,
      });
      setToast({
        open: true,
        msg: `Status set to ${newStatus}`,
        sev: "success",
      });
      await loadPurchases(); // Refresh table with canonical data
    } catch (err) {
      setRows(prevRows); // rollback
      setToast({ open: true, msg: "Failed to update status", sev: "error" });
    }
  };

  /* ---------- Step 3: Delete Purchase ---------- */
  const handleDelete = async () => {
    if (!deleteRow?.id) return;
    setDeleting(true);

    const { error } = await deleteJSON(`/api/purchase/${deleteRow.id}`);

    if (error) {
      setToast({ open: true, msg: "Failed to delete purchase", sev: "error" });
      setDeleting(false);
      return;
    }

    setToast({ open: true, msg: "Purchase deleted", sev: "success" });
    setDeleteRow(null);
    setDeleting(false);

    // Refresh table after deletion
    loadPurchases();
  };

  /* ---------- Step 4: Columns ---------- */
  const columns = [
    {
      field: "no",
      headerName: "No.",
      width: 80,
      sortable: false,
      valueGetter: (p) => p.api.getRowIndexRelativeToVisibleRows(p.id) + 1,
    },
    {
      field: "supplierName",
      headerName: "Supplier",
      flex: 1,
      minWidth: 200,
    },

    {
      field: "warehouseName",
      headerName: "Warehouse",
      flex: 1,
      minWidth: 200,
    },

    // {
    //   field: "subTotal",
    //   headerName: "Sub Total",
    //   width: 130,
    //   valueGetter: (p) => fmt(p.row.subTotal),
    // },
    // {
    //   field: "vat",
    //   headerName: "VAT",
    //   width: 130,
    //   valueGetter: (p) => fmt(p.row.vat),
    // },
    // {
    //   field: "totalVatedAfterDiscount",
    //   headerName: "Total Inc.",
    //   width: 130,
    //   valueGetter: (p) => fmt(p.row.totalVatedAfterDiscount),
    // },
    // {
    //   field: "withholdingAmount",
    //   headerName: "Withholding",
    //   width: 130,
    //   valueGetter: (p) => fmt(p.row.withholdingAmount),
    // },
    {
      field: "purchaseDate",
      headerName: "Date",
      width: 160,
      // valueGetter: (p) => new Date(p.row.purchaseDate).toLocaleDateString(),
      valueGetter: (p) => dayjs.utc(p.row.purchaseDate).format("YYYY-MM-DD"),
    },
    {
      field: "status",
      headerName: "Status",
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        const colorMap = {
          pending: "warning",
          approved: "success",
          rejected: "error",
        };
        const label = (row.status || "pending").toLowerCase();

        return (
          <Button
            size="small"
            variant="outlined"
            color={colorMap[label] || "inherit"}
            onClick={(e) => setStatusMenu({ anchorEl: e.currentTarget, row })}
          >
            {label}
          </Button>
        );
      },
    },

    {
      field: "actions",
      headerName: "Actions",
      width: 140,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Details">
            <IconButton size="small" onClick={() => setDetailRow(row)}>
              <InfoOutlinedIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => navigate(`/purchase/edit/${row.id}`)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <Box sx={{ position: "relative" }}>
              <IconButton
                size="small"
                color="error"
                onClick={() => setDeleteRow(row)}
                disabled={deleting && deleteRow?.id === row.id}
              >
                <DeleteOutlineIcon />
              </IconButton>
              {deleting && deleteRow?.id === row.id && (
                <CircularProgress
                  size={26}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    mt: "-13px",
                    ml: "-13px",
                  }}
                />
              )}
            </Box>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const gridSx = {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
    "& .MuiCheckbox-root": { color: `${colors.greenAccent[200]} !important` },
    "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
      color: `${colors.grey[100]} !important`,
    },
  };

  /* ---------- Render ---------- */
  return (
    <Box m="20px">
      <Header title="PURCHASES" subtitle="View and Manage Purchases" />

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate("/purchasereceipt")}
        >
          New Receipt
        </Button>
      </Box>

      <SimpleTable
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        loading={loading}
        height="70vh"
        sx={gridSx}
      />

      <Menu
        anchorEl={statusMenu.anchorEl}
        open={Boolean(statusMenu.anchorEl)}
        onClose={() => setStatusMenu({ anchorEl: null, row: null })}
      >
        {["approved", "rejected"].map((opt) => (
          <MenuItem
            key={opt}
            onClick={() => {
              const row = statusMenu.row;
              setStatusMenu({ anchorEl: null, row: null });
              handleStatusChange(row, opt);
            }}
          >
            {opt}
          </MenuItem>
        ))}
      </Menu>

      {/* DELETE DIALOG */}
      <Dialog
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Purchase</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete this purchase?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRow(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            disabled={deleting}
            color="error"
            variant="contained"
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* DETAILS DIALOG */}
      {detailRow && (
        <Dialog
          open={true}
          onClose={() => setDetailRow(null)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>Purchase Details</DialogTitle>
          <DialogContent dividers>
            <Box display="flex" flexDirection="column" gap={1.5}>
              <Typography>
                <b>Supplier:</b> {detailRow.supplier?.name || "—"}
              </Typography>

              <Typography>
                <b>Warehouse:</b> {detailRow.warehouse?.name || "—"}
              </Typography>
              <Typography>
                <b>FS No:</b> {detailRow.fsNo || "—"}
              </Typography>
              {/* <Typography><b>Date:</b> {new Date(detailRow.purchaseDate).toLocaleString()}</Typography> */}
              <Typography>
                <b>Date:</b>{" "}
                {dayjs
                  .utc(detailRow.purchaseDate)
                  .format("YYYY-MM-DD HH:mm [UTC]")}
              </Typography>
              <Typography>
                <b>Description:</b> {detailRow.description || "—"}
              </Typography>
              <Typography>
                <b>Status:</b> {detailRow.status}
              </Typography>

              {/* <Divider sx={{ my: 1 }} /> */}
              {/* <Typography variant="subtitle1">
                <b>Totals</b>
              </Typography>
              <Typography>
                <b>Subtotal:</b> {fmt(detailRow.subTotal)}
              </Typography>
              <Typography>
                <b>VAT:</b> {fmt(detailRow.vat)}
              </Typography>
              <Typography>
                <b>Total After Discount:</b>{" "}
                {fmt(detailRow.totalVatedAfterDiscount)}
              </Typography>
              <Typography>
                <b>Withholding:</b> {fmt(detailRow.withholdingAmount)}
              </Typography> */}

              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1">
                <b>Items</b>
              </Typography>
              {detailRow.items?.map((item) => (
                <Box
                  key={item.id}
                  p={1}
                  border="1px solid #ddd"
                  borderRadius={1}
                  mb={1}
                >
                  <Typography>
                    <b>Name:</b> {item.item?.name || "—"}
                  </Typography>

                  <Typography>
                    <b>Qty:</b> {item.quantity}
                  </Typography>

                  <Typography>
                    <b>Unit Price:</b> {fmt(item.unitPrice)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => window.print()}
              variant="outlined"
              startIcon={<PrintIcon />}
            >
              Print
            </Button>
            <Button onClick={() => setDetailRow(null)} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{ mt: 5 }}
      >
        <Alert
          onClose={() => setToast((s) => ({ ...s, open: false }))}
          severity={toast.sev}
          sx={{ width: "100%" }}
        >
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
